import type { Client } from "@/apis/client/client-apis";
import { useClient, usePlanStatuses, usePlanTypes } from "@/v2/queries";
import {
  type BaseEntityModalResult,
  createEntityModalHook,
} from "./createEntityModalHook";

const useBaseClientModal = createEntityModalHook<Client, number>({
  useQueryHook: useClient,
  getEntityId: (client) => client.id,
});

export interface UseClientModalResult
  extends Omit<BaseEntityModalResult<Client, number>, "entity"> {
  client: Client | null;
  planTypes: string[];
  planStatuses: string[];
  isLoadingDropdowns: boolean;
}

/**
 * Hook for managing client modal state and data loading.
 *
 * Handles:
 * - Modal visibility state
 * - Loading client data via TanStack Query
 * - Loading related dropdown data (plan types, statuses) in parallel
 * - Modal opens as soon as client loads (prevents stale cached data)
 */
export function useClientModal(): UseClientModalResult {
  const baseModal = useBaseClientModal();
  const { data: planTypes, isLoading: isLoadingPlanTypes } = usePlanTypes();
  const { data: planStatuses, isLoading: isLoadingPlanStatuses } =
    usePlanStatuses();

  return {
    isOpen: baseModal.isOpen,
    client: baseModal.entity,
    isLoading: baseModal.isLoading,
    planTypes: planTypes ?? [],
    planStatuses: planStatuses ?? [],
    open: baseModal.open,
    close: baseModal.close,
    isLoadingDropdowns: isLoadingPlanTypes || isLoadingPlanStatuses,
  };
}
